import {
    AppIcon,
    BarGraphIcon,
    BellIcon,
    BookIcon,
    BriefcaseIcon,
    CalendarIcon,
    CheckIcon,
    CompassIcon,
    GlobeIcon,
    PinIcon,
    RosterIcon,
    SearchIcon,
    SettingsIcon,
    StarIcon,
    TicketIcon,
    UserIcon,
} from "@xola/icons";
import { Button, Sidebar } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { CanvasType, Plugin, PluginTrigger, TriggerType } from "types";
import { useEmbeddedAppStore } from "./embeddedAppStore";
import * as hostRpc from "./hostRpc";
import { useInstalledPluginsFromStore } from "@/stores/globalStore";

const ICON_MAP: Record<string, React.ReactElement> = {
    app: <AppIcon />,
    barGraph: <BarGraphIcon />,
    bell: <BellIcon />,
    book: <BookIcon />,
    briefcase: <BriefcaseIcon />,
    beaker: <AppIcon />,
    calendar: <CalendarIcon />,
    check: <CheckIcon />,
    compass: <CompassIcon />,
    globe: <GlobeIcon />,
    map: <CompassIcon />,
    pin: <PinIcon />,
    roster: <RosterIcon />,
    search: <SearchIcon />,
    settings: <SettingsIcon />,
    star: <StarIcon />,
    ticket: <TicketIcon />,
    user: <UserIcon />,
};

const resolveIcon = (name: string | undefined): React.ReactElement | undefined => (name ? ICON_MAP[name] : undefined);

// ui-kit ships untyped, so the supported color names are mirrored here rather than imported.
type ButtonColor = "primary" | "secondary" | "danger" | "warning" | "success" | "common";

interface TriggerMatch {
    plugin: Plugin;
    trigger: PluginTrigger;
}

// Exported so callers that must know whether a location renders anything (e.g. to decide whether
// to draw surrounding chrome) share this matching rule instead of duplicating it.
// automatic and canvasType=none apps are always-on - EmbeddedAppRoot manages them
export const findTriggersAtLocation = (plugins: Plugin[], locationId: string): TriggerMatch[] =>
    plugins.flatMap((plugin) => {
        if (!plugin.ui?.isEmbeddedApp || plugin.ui.canvasType === CanvasType.NONE) {
            return [];
        }
        const trigger = plugin.ui.triggers?.find((t) => t.location === locationId && t.type !== TriggerType.AUTOMATIC);
        return trigger ? [{ plugin, trigger }] : [];
    });

interface TriggerItemProps {
    plugin: Plugin;
    trigger: PluginTrigger;
    context: Record<string, unknown>;
    buttonVariant: "link" | "outline" | "solid";
    buttonSize: "small" | "medium" | "large";
    buttonColor: ButtonColor;
    buttonClassName?: string;
}

const TriggerItem = ({
    plugin,
    trigger,
    context,
    buttonVariant,
    buttonSize,
    buttonColor,
    buttonClassName,
}: TriggerItemProps) => {
    const history = useHistory();
    const actions = useEmbeddedAppStore.getState().actions;

    // Triggers placed in a left-menu location always render as menu items, regardless of the
    // declared trigger type, since a Button does not fit the sidebar submenu layout.
    const isLeftMenu = trigger.location?.endsWith(".leftMenu") ?? false;

    // Fullscreen apps have no in-place canvas (EmbeddedAppRoot filters them out), so they must
    // navigate to their dedicated route instead of calling openCanvas, for both menu and button.
    const handleClick = useMemoizedFn(() => {
        hostRpc.notifyTrigger(plugin.id, {
            location: trigger.location ?? "",
            triggerType: trigger.type,
            route: history.location.pathname,
            context,
        });
        if (plugin.ui?.canvasType === CanvasType.FULLSCREEN) {
            history.push(`/embeddedApps/${plugin.id}`);
        } else {
            actions.openCanvas(plugin.id, context);
        }
    });

    if (trigger.type === TriggerType.MENU_ITEM || isLeftMenu) {
        return (
            <Sidebar.Link isSubMenuItem onClick={handleClick}>
                {trigger.label}
            </Sidebar.Link>
        );
    }

    return (
        <Button
            variant={buttonVariant}
            size={buttonSize}
            color={buttonColor}
            icon={resolveIcon(trigger.icon)}
            className={buttonClassName}
            onClick={handleClick}
        >
            {trigger.label}
        </Button>
    );
};

interface EmbeddedAppTriggerLocationProps {
    id: string;
    context?: Record<string, unknown>;
    buttonVariant?: "link" | "outline" | "solid";
    buttonSize?: "small" | "medium" | "large";
    buttonColor?: ButtonColor;
    buttonClassName?: string;
}

export const EmbeddedAppTriggerLocation = ({
    id,
    context = {},
    buttonVariant = "outline",
    buttonSize = "medium",
    buttonColor = "secondary",
    buttonClassName,
}: EmbeddedAppTriggerLocationProps) => {
    const plugins = useInstalledPluginsFromStore();

    const matches = useMemo(() => findTriggersAtLocation(plugins, id), [plugins, id]);

    return (
        <>
            {matches.map(({ plugin, trigger }) => (
                <TriggerItem
                    key={plugin.id}
                    plugin={plugin}
                    trigger={trigger}
                    context={context}
                    buttonVariant={buttonVariant}
                    buttonSize={buttonSize}
                    buttonColor={buttonColor}
                    buttonClassName={buttonClassName}
                />
            ))}
        </>
    );
};
